/* eslint-disable jsdoc/no-undefined-types */
import get from 'lodash.get';

import cartData from '#shared/util/cartData.js';
import isObject from '#shared/util/isObject.js';
import invalidCartRedirectMiddleware from '#shared/middleware/invalidCartRedirectMiddleware.js';
import getCartMiddleware from '#shared/middleware/getCartMiddleware.js';

import cButton from '#themes/default/default/components/button/button.js';
import cButtons from '#themes/default/default/components/buttons/buttons.js';
import cContentLayout from '#themes/default/default/layouts/contentLayout/contentLayout.js';
import cHeader from '#themes/default/default/components/header/header.js';
import cSkeleton from '#themes/default/default/components/skeleton/skeleton.js';

import isValid from '#frontend/util/isValid.js';
import focusFirstError from '#frontend/util/focusFirstError.js';

import template from './paymentDetails.html';

/**
 * @memberof PaymentDetailsPage
 */
export default {
	...cartData,
	middleware: [
		invalidCartRedirectMiddleware,
		getCartMiddleware
	],
	components: {
		cButton,
		cButtons,
		cContentLayout,
		cHeader,
		cSkeleton
	},
	meta() {
		return {
			title: `${this.t('global_metaTitle')} - ${this.t('paymentDetails_metaTitle')}`
		};
	},
	data() {
		return {
			formValid: false
		};
	},
	computed: {
		isCreditCard() {
			return isObject(this.selectedPayment) && this.selectedPayment.type === 'creditCard';
		},
		isCreditCardTouched() {
			return isObject(this.form) && this.form.creditCard?.touched;
		}
	},
	mounted() {
		this.tracking.checkout({
			url: this.url,
			page: this.page,
			order: this.order,
			theme: this.theme
		});
		this.tracking.paymentDetails({
			app: this.app,
			url: this.url,
			page: this.page,
			order: this.order,
			theme: this.theme
		});
		this.utils.hideLoader();
	},
	methods: {
		async handleCheckoutFormSubmit() {
			// Present loading state to the user
			this.utils.showLoader();
			// Submit the order and return the result
			if (await this.startSubmitFlow()) {
				await this.$router.push(this.getLink('confirm-order')).catch(() => {});
			}
		},
		showPaymentFailedModal() {
			const cvcElement = this.selectedPayment.cardcvv;
			this.utils.setPaymentFailed();
			this.utils.hideLoader();
			this.$store.commit('SET_MODAL', 'paymentFailed');
			if (cvcElement) {
				cvcElement.clear();
			}
		},
		validateForm() {
			this.formValid = isValid('form');
		},
		focusInvalidCCField() {
			const creditCardFields = get(this.paymentContainer, '$refs.creditCardPaymentMethod.$refs');
			focusFirstError(creditCardFields, this.theme?.provider.name);
		},
		async storeInfoSubmitAttempt(isSelected, isTouched, paymentMethod = '', focusInvalidMethod = () => {}) {
			// Store information about PM submit attempt
			if (isSelected) {
				this.$store.commit('SET_FORM', {
					[paymentMethod]: {
						submitted: true
					}
				});
				// Do not continue if PM hasn't been filled or interacted with
				if (!isTouched) {
					await this.$nextTick();
					focusInvalidMethod();
					this.utils.hideLoader();
					throw new Error('Customer hasnt interacted with PM iframe yet but tried to submit the order');
				}
			}
		},
		async startSubmitFlow() {
			// Validate customer fields
			this.validateForm();
			try {
				// Store information about Credit Card submit attempt
				await this.storeInfoSubmitAttempt(this.isCreditCard, this.isCreditCardTouched, 'creditCard', this.focusInvalidCCField);
			} catch {
				// Cancel the submit attempt if the customer didn't interacted with the iframe
				return false;
			}
			let paymentSource;
			if (this.formValid) {
				// Store credit card interaction details for further use
				const ccFormObject = this.form.creditCard;
				// Block submit if CVC hasn't been touched (Autofill)
				if (this.isCreditCard && ccFormObject && !ccFormObject.cvcTouched) {
					this.utils.hideLoader();
					return false;
				}
				// Return touched state if it's already been interacted with the field
				if (this.isCreditCard && ccFormObject && ccFormObject?.touched) {
					this.$store.commit('SET_FORM', {
						creditCard: {
							touched: true
						}
					});
				}
				// Generate payment source
				try {
					// Apply form details (installments, cpj/cnpj)
					await this.utils.applyForm(this.form);
					paymentSource = await this.paymentContainer.getPaymentSource();
				} catch {
					this.showPaymentFailedModal();
					return false;
				}
			}
			// If customer billing form is invalid or payment source was not generated, cancel the order submit
			if (!this.formValid || !paymentSource) {
				this.focusInvalidCCField();
				this.utils.hideLoader();
				return false;
			}
			// Apply the payment method source generated for current payment selection
			try {
				await this.utils.applyPayment(paymentSource);
			} catch {
				this.showPaymentFailedModal();
				return false;
			}
			// Store information about Credit Card complementation status
			if (this.isCreditCard) {
				this.$store.commit('SET_FORM', {
					creditCard: {
						completed: true,
						submitted: true,
						touched: false
					}
				});
			}
			// Continue to the next checkout step
			return true;
		}
	},
	template
};
